import { gql, useQuery } from '@apollo/client';
import { Box, Stack, useBreakpointValue } from '@chakra-ui/react';
import * as React from 'react';

import { Footer, FooterButton, FooterButtonLabel } from '@/components/Footer';
import { NavigationButton } from '@/components/NavigationButton';
import { MembershipStatus } from '@/graphql-types/globalTypes';
import { useElementHeight } from '@/hooks/useElementHeight';
import { useWindowHeight } from '@/hooks/useWindowHeight';
import { UserIcon } from '@/imports/ui/chakra/feather';
import {
  Blueprint,
  BlueprintPortal,
  useBlueprint,
} from '@/layouts/components/Blueprint';

import {
  Strawberry_OrganizationSearch,
  Strawberry_OrganizationSearchVariables,
} from './graphql-types/Strawberry_OrganizationSearch';
import { OrganiizationIcon } from '@/imports/ui/chakra/customIcon/oorganization';
import { MemberIcon } from '@/imports/ui/chakra/customIcon/MemberIcon';

const scrollbar = {
  '::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
};

const StrawberryContext = React.createContext<{
  footerHeight: number;
  hasCompanies: boolean;
}>({
  footerHeight: 0,
  hasCompanies: false,
});

const useStrawberry = () => React.useContext(StrawberryContext);

const ORGANIZATIONS = gql`
  query Strawberry_OrganizationSearch($input: OrganizationSearchInput!) {
    organizationSearch(input: $input) {
      totalCount
    }
  }
`;

const StrawberryNav: React.FC = (props) => {
  const windowHeight = useWindowHeight();
  const { headerHeight } = useBlueprint();
  const { hasCompanies } = useStrawberry();

  return (
    <Box
      alignSelf="flex-start"
      as="nav"
      display={{ base: 'none', lg: 'block' }}
      h={`${windowHeight - headerHeight}px`}
      mx="-1"
      overflowY="auto"
      position="sticky"
      px="1"
      py="4"
      top={`${headerHeight}px`}
      w={{ base: 60, xl: 72 }}
      sx={scrollbar}
    >
      {hasCompanies ? (
        <Stack spacing="2">
          <NavigationButton href="/members" leftIcon={<MemberIcon boxSize="5" color="primary.500" />} isExact newView={true}>
            Individuals
          </NavigationButton>
          <NavigationButton
            href="/members/companies"
            leftIcon={<OrganiizationIcon boxSize="5" color="primary.500" mr="2px" />} newView={true}>
            Organizations
          </NavigationButton>
        </Stack>
      ) : null}
    </Box>
  );
};

export const StrawberryMain: React.FC<{ isFullWidth?: boolean }> = (props) => {
  return (
    <Box as="main" py="4" px="1" mx="-1" flex="1" overflowY="auto">
      {props.children}
    </Box>
  );
};

export const StrawberryAside: React.FC = (props) => {
  const windowHeight = useWindowHeight();
  const { headerHeight } = useBlueprint();
  const { footerHeight } = useStrawberry();

  const height = useBreakpointValue({
    base: windowHeight - headerHeight - footerHeight,
    lg: windowHeight - headerHeight,
  });

  return (
    <Box
      alignSelf="flex-start"
      as="aside"
      display={{ base: 'none', md: 'block' }}
      h={`${height}px`}
      mx="-1"
      overflowY="auto"
      position="sticky"
      px="1"
      py="4"
      top={`${headerHeight}px`}
      w={{ base: 72, lg: 60, xl: 72 }}
      sx={scrollbar}
    >
      {props.children}
    </Box>
  );
};

export const StrawberryPortal = BlueprintPortal;

export const Strawberry: React.FC = (props) => {
  const footer = useElementHeight();

  const query = useQuery<
    Strawberry_OrganizationSearch,
    Strawberry_OrganizationSearchVariables
  >(ORGANIZATIONS, {
    variables: {
      input: {
        limit: 1,
        offset: 0,
        filter: {
          membershipStatuses: [MembershipStatus.MEMBER],
          hidden: false,
        },
      },
    },
  });

  const hasCompanies = query?.data?.organizationSearch.totalCount
    ? query.data.organizationSearch.totalCount > 0
    : false;

  return (
    <StrawberryContext.Provider
      value={{
        footerHeight: footer.height,
        hasCompanies,
      }}
    >
      <Blueprint>
        <Box
          display="flex"
          gap={{ base: 2, md: 4 }}
          maxW="container.xl"
          mx="auto"
          px="4"
          mb={`${footer.height}px`}
        >
          <StrawberryNav />
          {props.children}
        </Box>

        {hasCompanies && (
          <Box
            display={{ base: 'block', lg: 'none' }}
            as="nav"
            position="fixed"
            bottom="0"
            left="0"
            right="0"
            ref={footer.ref}
            zIndex="docked"
          >
            <Footer>
              <FooterButton href="/members" isExact>
                <MemberIcon boxSize="5" />
                <FooterButtonLabel>Individuals</FooterButtonLabel>
              </FooterButton>
              <FooterButton href="/members/companies">
                <OrganiizationIcon boxSize="5" />
                <FooterButtonLabel>Organizations</FooterButtonLabel>
              </FooterButton>
            </Footer>
          </Box>
        )}
      </Blueprint>
    </StrawberryContext.Provider>
  );
};
